<template>
  <div
    v-if="modelValue"
    class="absolute flex flex-col h-full px-1 py-2 overflow-hidden rounded right-2 w-96 bg-white shadow-toolbar top-20"
    style="height: calc(100% - 100px);"
  >
    <div class="flex w-full p-4">
      <div
        class="pt-1 text-xl font-medium leading-6 text-center grow text-dark-75"
        v-text="t('whiteboard.history.title')"
      />
      <tx-button type="icon" faicon="fa-light fa-xmark" @click="doClose" />
    </div>
    <div class="flex h-8 text-center w-full">
      <tx-tabs v-model="activeTab" class="grow" :tabs="Object.values(tabs)" />
    </div>
    <div class="flex flex-col w-full h-full overflow-hidden">
      <div v-show="activeTab === tabs.activities.key" class="flex flex-col w-full h-full overflow-hidden">
        <activities-list ref="activitiesListRef" :whiteboard="props.whiteboard" :permission="props.permission" />
      </div>
      <div v-show="activeTab === tabs.versions.key" class="flex flex-col w-full h-full overflow-hidden">
        <versions-list ref="versionsListRef" :whiteboard-details="props.whiteboardDetails" :whiteboard="props.whiteboard" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { nextTick, ref, watch } from 'vue'
import type Whiteboard from '../../services/whiteboard'
import ActivitiesList from './ActivitiesList.vue'
import VersionsList from './VersionsList.vue'
import TxTabs from '@/shared/components/TxTabs.vue'
import TxButton from '@/shared/components/TxButton.vue'
import type { WhiteboardDetailsModel } from '@/api/t1/model/whiteboardModel'

interface IProps {
  modelValue?: boolean
  whiteboardDetails?: WhiteboardDetailsModel
  whiteboard?: Whiteboard
  permission: WhiteboardUserPermissionType
}

const props = withDefaults(defineProps<IProps>(), { modelValue: false })

const emit = defineEmits<{
  (e: 'update:modelValue', val: boolean): void
  (e: 'toggleFullscreen')
}>()

const { t } = useI18n()

const activitiesListRef = ref<InstanceType<typeof ActivitiesList> | null>(null)
const versionsListRef = ref<InstanceType<typeof VersionsList> | null>(null)

const tabs: Record<string, ITabItem> = {
  activities: { key: 'activities', label: 'whiteboard.history.activities', visible: true },
  versions: { key: 'versions', label: 'whiteboard.history.versions', visible: true },
}

const activeTab = ref(tabs.activities.key)

function doClose() {
  emit('update:modelValue', false)
}

watch(() => props.modelValue, (v) => {
  if (v) {
    nextTick(() => {
      activitiesListRef.value?.loadData()
      versionsListRef.value?.loadData()
    })
  }
})
</script>
